import { find, get, isEmpty, isEqual, isObject, unset } from 'lodash';
import { ObjectSchema } from 'yup';
import dayjs from 'dayjs';

import {
  FieldError,
  FieldErrors,
  FormComponentType,
  FormPanelComponentType,
  FormStatus,
  FormType,
  ICurrentPosition,
  IElementSchema,
  IFormData,
  IFormDataSchema,
  IFormErrors,
  IFormPayload,
  IFormSchema,
  IFormState,
  IPageSchema,
  IFormDictionary,
  ISectionSchema,
  IValidatableSchema,
  Optional,
  ValidationRuleType,
} from './../types/form';
import { IMasterTableData } from './../types/models/master-table';
import { IPersona } from './../types/models/persona';
import { hasRequiredValidation, QUIZ_FIELD_NAME } from './form-builder';

import { ARTICLE, ASSETS_URL, PATH_SEPARATOR } from '../constants';
import { programLongName, programShortName } from '../siteContent';
import { StageType } from '../types/models/program-management';

export const FORM_DEBUG_QUERY_KEY = 'formDebug';
export const DEFAULT_FORM_SCHEMA = {
  sections: [],
};
export const DEFAULT_FORM_DATA: IFormDataSchema = {
  data: {},
};
export const DEFAULT_FORM_STATE = {
  currentPosition: { sectionIndex: 0, pageIndex: 0 },
  sections: [],
};

export const ETHNICITIES_PROFILE_FIELD_NAME = 'ethnicities';
export const ETHNICITIES_PROFILE_FIELD_NONE_OF_ABOVE_OPTION_VALUE = 'none';

export const MAX_QUIZ_FORM_RETRY_COUNT = 3;

export const QUIZ_ELEMENTS_PATH = 'sections[0].pages[1].elements';

// TODO: update `CERTIFICATION_ASSETS_PATH` if public asset location changes
const CERTIFICATION_ASSETS_PATH = `${ASSETS_URL}/public/certification`;
export const QUIZ_FORM_DICTIONARY = new Map<StageType, IFormDictionary>([
  [
    StageType.READER_CERTIFICATION,
    {
      roleName: 'Reader',
      packetUrl:
        process.env.GATSBY_READER_CERTIFICATION_CALIBRATION_PACKET_URL ||
        `${CERTIFICATION_ASSETS_PATH}/calibration-packets/reader-calibration-packet.pdf`,
      videoUrl:
        process.env.GATSBY_READER_CERTIFICATION_VIDEO_URL ||
        `${CERTIFICATION_ASSETS_PATH}/videos/reader-certification-video.mp4`,
      nda: {
        forms: [
          `To the best of my knowledge, I do not have friends or relatives applying for ${ARTICLE} ${programLongName} Scholarship.`,
          `I cannot review any materials of an applicant who is known to me and agree that I will notify ${programLongName} immediately if I am presented with such materials. Once notification takes place, I agree to recuse myself from reading said application.`,
          `I am not an employee of ${programLongName}, my time spent as ${ARTICLE} ${programLongName} Reader is voluntary.`,
          `I understand that any and all materials pertaining to the ${programLongName} Scholarship Application are the confidential property of ${programLongName}.`,
        ],
        agreements: [
          `I, the undersigned, understand that, by virtue of my role as ${ARTICLE} ${programLongName} Reader, will have access to scholarship applications containing written, personal stories of applicants. My role as ${ARTICLE} ${programLongName} reader will be to evaluate applicants based on the rubric and training information provided to me by the Hispanic Scholarship Fund.`,
          `As a volunteer ${programLongName} Reader, I understand that I will have access to confidential information, both verbal and written, relating to students who submit ${programLongName} Scholarship Applications.`,
          `I understand, and agree, that all such information is to be treated as strictly confidential and discussed only within the boundaries of my volunteer position as ${ARTICLE} ${programLongName} Reader.`,
          `Additionally, I agree not to discuss or disclose these confidential matters after I have completed my tasks as ${ARTICLE} ${programLongName} Reader. Further, I understand that breach of this agreement shall constitute grounds for and may result in termination of my volunteer status as ${ARTICLE} ${programLongName} Reader.`,
        ],
        title: 'Confidentiality and Non - Disclosure Agreement',
        instructiveLabel: `In connection to my review of the ${programLongName} Applicants and review of related materials received from the student applicants, I hereby agree, represent and warrant as follows:`,
        ndaListTitle: 'Please read and sign the following Confidentiality and Non-Disclosure Agreement',
        ndaCheckboxTitle:
          'Please sign below to indicate your acceptance and agreement with these terms, representations and warranties outlined above.',
      },
    },
  ],
  [
    StageType.INTERVIEWER_CERTIFICATION,
    {
      roleName: 'Interviewer',
      packetUrl:
        process.env.GATSBY_INTERVIEWER_CERTIFICATION_CALIBRATION_PACKET_URL ||
        `${CERTIFICATION_ASSETS_PATH}/calibration-packets/interviewer-calibration-packet.pdf`,
      videoUrl:
        process.env.GATSBY_INTERVIEWER_CERTIFICATION_VIDEO_URL ||
        `${CERTIFICATION_ASSETS_PATH}/videos/interviewer-certification-video.mp4`,
      nda: {
        forms: [
          `To the best of my knowledge, I do not have friends or relatives applying for the ${programLongName}.`,
          `I cannot review any materials of an applicant who is known to me and agree that I will notify the ${programShortName} administrator immediately if I am presented with such applicant. Once such notification takes place, I agree to recuse myself from interviewing said applicant.`,
          `I am not an employee of ${programShortName}. My time spent as a ${programShortName} Interviewer is voluntary and I will not be compensated or receive any wages, salary or other benefits for my participation in the ${programShortName} Interviews.`,
          `I understand that any and all materials pertaining to the ${programShortName} application are the confidential property of ${programShortName} Program.`,
        ],
        agreements: [
          `I, the undersigned, understand that, by virtue of my role as a ${programShortName} Interviewer, will have access to scholarship applicants’ information. My role as a ${programShortName} Interviewer will be to evaluate applicants based on the rubric and training information provided to me by the ${programLongName}.`,
          `As a volunteer ${programShortName} Interviewer, I understand that I will have access to confidential information, both verbal and written, relating to students who submit ${programShortName} Scholarship Applications.`,
          `I understand, and agree, that all such information is to be treated as strictly confidential and discussed only within the boundaries of my volunteer position as a ${programShortName} Interviewer.`,
          `Additionally, I agree not to discuss or disclose these confidential matters after I have completed my tasks as a ${programShortName} Interviewer. Further, I understand that breach of this agreement shall constitute grounds for and will result in termination of my volunteer status as a ${programShortName} Interviewer.`,
          `Please sign below to indicate your acceptance and agreement with these terms, representations and warranties outlined above.`,
        ],
        title: 'Confidentiality and Non - Disclosure Agreement',
        instructiveLabel: `In connection to my Interviews of the ${programLongName} Applicants and review of related materials received from the student applicants, I hereby agree, represent and warrant as follows:`,
        ndaListTitle: 'Please read and sign the following Confidentiality and Non-Disclosure Agreement',
        ndaCheckboxTitle:
          'Please sign below to indicate your acceptance and agreement with these terms, representations and warranties outlined above.',
      },
    },
  ],
  [
    StageType.COACH_CERTIFICATION,
    {
      roleName: 'coach',
      packetUrl:
        process.env.GATSBY_COACH_CERTIFICATION_CALIBRATION_PACKET_URL ||
        `${CERTIFICATION_ASSETS_PATH}/calibration-packets/coach-calibration-packet.pdf`,
      videoUrl:
        process.env.GATSBY_COACH_CERTIFICATION_VIDEO_URL ||
        `${CERTIFICATION_ASSETS_PATH}/videos/coach-certification-video.mp4`,
      nda: {
        forms: [
          `To the best of my knowledge, I do not have friends or relatives participating in the Career Coach program.`,
          `I cannot serve as a Career Coach to a student who is known to me and agree that I will notify the ${programLongName} (“${programShortName}”) immediately if I am presented with such a situation. Once such notification takes place, I agree to recuse myself from serving as a Career Coach to such student.`,
          `I am not an employee of ${programShortName}. My time spent as a Career Coach is voluntary and I will not be compensated or receive any wages, salary or other benefits for my participation as a Career Coach.`,
          `I understand that any and all materials pertaining to the Career Coach program are the confidential property of ${programShortName}.`,
        ],
        agreements: [
          `I, the undersigned, understand that, by virtue of my role as a Career Coach, I will have access to personal stories and information of Coachees. My role as a Career Coach will be to coach students based on the rubric and training information provided to me by the Career Coach Program.`,
          `As a volunteer Career Coach, I understand that I will have access to confidential information, both verbal and written, relating to students who participate in the Career Coach program.`,
          `I understand, and agree, that all such information is to be treated as strictly confidential and discussed only within the boundaries of my volunteer position as a Career Coach.`,
          `Additionally, I agree not to discuss or disclose these confidential matters after I have completed my tasks as a Career Coach. Further, I understand that breach of this agreement shall constitute grounds for and will result in termination of my volunteer status as a Career Coach.`,
        ],
        title: 'Confidentiality and Non - Disclosure Agreement',
        instructiveLabel: `In connection to my review of the ${programLongName} Applicants and review of related materials received from the student applicants, I hereby agree, represent and warrant as follows:`,
        ndaListTitle: 'Please read and sign the following Confidentiality and Non-Disclosure Agreement',
        ndaCheckboxTitle:
          'Please sign below to indicate your acceptance and agreement with these terms, representations and warranties outlined above.',
      },
    },
  ],
  [
    StageType.MENTOR_CERTIFICATION,
    {
      roleName: 'mentor',
      packetUrl:
        process.env.GATSBY_MENTOR_CERTIFICATION_CALIBRATION_PACKET_URL ||
        `${CERTIFICATION_ASSETS_PATH}/calibration-packets/mentor-calibration-packet.pdf`,
      videoUrl:
        process.env.GATSBY_MENTOR_CERTIFICATION_VIDEO_URL ||
        `${CERTIFICATION_ASSETS_PATH}/videos/mentor-certification-video.mp4`,
      nda: {
        forms: [
          `To the best of my knowledge, I do not have friends or relatives participating in the MentorMatch program.`,
          `I cannot serve as a Mentor to a student who is known to me and agree that I will notify the ${programLongName} (“${programShortName}”) immediately if I am presented with such a situation. Once such notification takes place, I agree to recuse myself from serving as a Mentor to such student.`,
          `I am not an employee of ${programShortName}. My time spent as a Mentor is voluntary and I will not be compensated or receive any wages, salary or other benefits for my participation as a Mentor.`,
          `I understand that any and all materials pertaining to the MentorMatch program are the confidential property of ${programShortName}.`,
        ],
        agreements: [
          'I, the undersigned, understand that, by virtue of my role as a Mentor, I will have access to personal stories and information of Mentees. My role as a Mentor will be to mentor students based on the rubric and training information provided to me by the MentorMatch program.',
          'As a volunteer Mentor, I understand that I will have access to confidential information, both verbal and written, relating to students who participate in the MentorMatch program.',
          'I understand, and agree, that all such information is to be treated as strictly confidential and discussed only within the boundaries of my volunteer position as a Mentor.',
          'Additionally, I agree not to discuss or disclose these confidential matters after I have completed my tasks as a Mentor. Further, I understand that breach of this agreement shall constitute grounds for and will result in termination of my volunteer status as a Mentor.',
        ],
        title: 'Confidentiality and Non - Disclosure Agreement',
        instructiveLabel: `In connection to my review of the ${programLongName} Applicants and review of related materials received from the student applicants, I hereby agree, represent and warrant as follows:`,
        ndaListTitle: 'Please read and sign the following Confidentiality and Non-Disclosure Agreement',
        ndaCheckboxTitle:
          'Please sign below to indicate your acceptance and agreement with these terms, representations and warranties outlined above.',
      },
    },
  ],
]);

export const APPLICATION_FORM_DICTIONARY = new Map<StageType, IFormDictionary>([
  [
    StageType.MENTOR_APPLICATION,
    {
      roleName: 'mentor',
      nda: {
        forms: [
          `I agree to treat Mentees, Mentors, MentorMatch program administrators, and ${programShortName} staff with dignity, respect, consideration, and to conduct myself in a mature and responsible manner at all times.`,
          `I agree to maintain a professional line of communication with my Mentee/Mentor and honor any commitments which may include, but is not limited to: scheduled appointments, assignments, training, tasks, survey requirements, and any other program specific action items.`,
          `I understand that a failure to respond to and/or ignore my Mentor/Mentee is considered disrespectful and may impact my ability to participate in the MentorMatch program.`,
          `I understand that MentorMatch administrators will make every effort to pair Mentors with Mentees with a similar educational background, professional interests, and other related experiences, but I acknowledge that a "perfect" pairing is not always possible and agree to maximize the opportunity.`,
          `I understand that ${programShortName} does not tolerate inappropriate, demeaning, discriminatory, or harassing behavior or speech. This includes offensive comments (verbal, written, or otherwise) related to gender, sexual orientation, race, religion, disability; inappropriate use of nudity and/or sexual images; deliberate intimidation, stalking or following; harassing materials, inappropriate physical contact, and unwelcome sexual attention.`,
          `If at any point, I am no longer able to participate in the program, I agree to notify the MentorMatch program administrator and my Mentor/Mentee as soon as possible.`,
          `I understand that ${programShortName} reserves the right to withdraw my involvement from the MentorMatch program if I do not meet program expectations or adhere to the Code of Conduct.`,
          `I agree to notify a MentorMatch program administrator immediately through the Message Center if I am subjected to unacceptable behavior or learn that someone else is being subjected to unacceptable behavior.`,
        ],
        agreements: [],
        title: 'Code of Conduct',
        instructiveLabel: `The ${programLongName} (“${programShortName}”) is committed to providing a safe, friendly, welcoming, and positive experience for all participants in MentorMatch. Please help us make this a reality, by adhering to the highest standard of behavior, at all times. To help us ensure a successful Mentor/Mentee relationship, you are required to sign this pledge to comply with the following code of conduct. `,
        ndaListTitle:
          'We fully expect all participants to follow the Code of Conduct throughout the MentorMatch program.',
        ndaCheckboxTitle:
          'With my signature below, I affirm that I have read and understand the MentorMatch Code of Conduct, and I agree to comply with all of the policies set forth within.',
      },
    },
  ],
  [
    StageType.MENTEE_APPLICATION,
    {
      roleName: 'mentee',
      nda: {
        forms: [
          `I agree to treat Mentees, Mentors, MentorMatch program administrators, and ${programShortName} staff with dignity, respect, consideration, and to conduct myself in a mature and responsible manner at all times.`,
          `I agree to maintain a professional line of communication with my Mentee/Mentor and honor any commitments which may include, but is not limited to: scheduled appointments, assignments, training, tasks, survey requirements, and any other program specific action items.`,
          `I understand that a failure to respond to and/or ignore my Mentor/Mentee is considered disrespectful and may impact my ability to participate in the MentorMatch program.`,
          `I understand that MentorMatch administrators will make every effort to pair Mentors with Mentees with a similar educational background, professional interests, and other related experiences, but I acknowledge that a "perfect" pairing is not always possible and agree to maximize the opportunity.`,
          `I understand that ${programShortName} does not tolerate inappropriate, demeaning, discriminatory, or harassing behavior or speech. This includes offensive comments (verbal, written, or otherwise) related to gender, sexual orientation, race, religion, disability; inappropriate use of nudity and/or sexual images; deliberate intimidation, stalking or following; harassing materials, inappropriate physical contact, and unwelcome sexual attention.`,
          `If at any point, I am no longer able to participate in the program, I agree to notify the MentorMatch program administrator and my Mentor/Mentee as soon as possible.`,
          `I understand that ${programShortName} reserves the right to withdraw my involvement from the MentorMatch program if I do not meet program expectations or adhere to the Code of Conduct.`,
          `I agree to notify a MentorMatch program administrator immediately through the Message Center if I am subjected to unacceptable behavior or learn that someone else is being subjected to unacceptable behavior.`,
        ],
        agreements: [],
        title: 'Code of Conduct',
        instructiveLabel: `The ${programLongName} (“${programShortName}”) is committed to providing a safe, friendly, welcoming, and positive experience for all participants in MentorMatch. Please help us make this a reality, by adhering to the highest standard of behavior, at all times. To help us ensure a successful Mentor/Mentee relationship, you are required to sign this pledge to comply with the following code of conduct. `,
        ndaListTitle:
          'We fully expect all participants to follow the Code of Conduct throughout the MentorMatch program.',
        ndaCheckboxTitle:
          'With my signature below, I affirm that I have read and understand the MentorMatch Code of Conduct, and I agree to comply with all of the policies set forth within.',
      },
    },
  ],
  [
    StageType.COACH_APPLICATION,
    {
      roleName: 'coach',
      nda: {
        forms: [
          `I agree to treat Coaches, Coachees, Career Coach program administrators, and ${programShortName} staff with dignity, respect, consideration, and to conduct myself in a mature and responsible manner at all times .`,
          `I agree to maintain a professional line of communication with my Coach/Coachee and honor any commitments which may include, but is not limited to: scheduled appointments, assignments, training, tasks, survey requirements, and any other program specific action items.`,
          `I understand that a failure to respond to and/or ignore my Coach/Coachee is considered disrespectful and may impact my ability to participate in the Career Coach program.`,
          `I understand that Career Coach administrators will make every effort to pair Coaches with Coachees with a similar educational background, professional interests, and other related experiences, but I acknowledge that a "perfect" pairing is not always possible and agree to maximize the opportunity.`,
          `I understand that ${programShortName} does not tolerate inappropriate, demeaning, discriminatory, or harassing behavior or speech. This includes offensive comments (verbal, written, or otherwise) related to gender, sexual orientation, race, religion, disability; inappropriate use of nudity and/or sexual images; deliberate intimidation, stalking or following; harassing materials, inappropriate physical contact, and unwelcome sexual attention.`,
          `If at any point, I am no longer able to participate in the program, I agree to notify the Career Coach program administrator and my Coach/Coachee as soon as possible.`,
          `I understand that ${programShortName} reserves the right to withdraw my involvement from the Career Coach program if I do not meet program expectations or adhere to the Code of Conduct.`,
          `I agree to notify a Career Coach program administrator immediately through the Message Center if I am subjected to unacceptable behavior or learn that someone else is being subjected to unacceptable behavior.`,
        ],
        agreements: [],
        title: 'Code of Conduct',
        instructiveLabel: `The ${programLongName} (“${programShortName}”) is committed to providing a safe, friendly, welcoming, and positive experience for all participants in Career Coach program. Please help us make this a reality, by adhering to the highest standard of behavior, at all times. To help us ensure a successful Coach/Coachee relationship, you are required to sign this pledge to comply with the following code of conduct.`,
        ndaListTitle:
          'We fully expect all participants to follow the Code of Conduct throughout the Career Coach program.',
        ndaCheckboxTitle:
          'With my signature below, I affirm that I have read and understand the Career Coach Code of Conduct, and I agree to comply with all of the policies set forth within.',
      },
    },
  ],
]);

export const NDA_FORM_DICTIONARY = new Map<StageType, IFormDictionary>([
  ...APPLICATION_FORM_DICTIONARY,
  ...QUIZ_FORM_DICTIONARY,
]);
export const CERTIFICATION_STAGE_TYPES = Array.from(QUIZ_FORM_DICTIONARY.keys());

export function isPanel(type: FormComponentType) {
  const panels: string[] = Object.values(FormPanelComponentType);
  return panels.includes(type);
}

export function isNonRepeatablePanel({ type, repeatable }: IValidatableSchema) {
  return type && isPanel(type) && !repeatable;
}

export function hasPlaceholder({ type }: IElementSchema) {
  return [
    FormComponentType.Text,
    FormComponentType.Textarea,
    FormComponentType.Dropdown,
    FormComponentType.AutoComplete,
  ].includes(type);
}

export function hasOptions(type: FormComponentType) {
  return [
    FormComponentType.Dropdown,
    FormComponentType.Checkboxgroup,
    FormComponentType.Radiogroup,
    FormComponentType.AutoComplete,
  ].includes(type);
}

export function hasStyle(type: FormComponentType) {
  return [
    FormComponentType.Text,
    FormComponentType.Dropdown,
    FormComponentType.Grade,
    FormComponentType.Number,
    FormComponentType.PhoneNumber,
    FormComponentType.SSN,
    FormComponentType.AutoComplete,
    FormComponentType.Date,
    FormComponentType.Hidden,
  ].includes(type);
}

export const MAX_WIDTH_FORM_COMPONENT = 354;

export const MAX_MOBILE_WIDTH_FORM_COMPONENT = 336;

export const MAX_PANEL_WIDTH_FORM_COMPONENT = 738;

export function hasValueType({ type }: IElementSchema) {
  return type === FormComponentType.Number || type === FormComponentType.Grade;
}

export function hasMaxLength({ type }: IElementSchema) {
  return [
    FormComponentType.Text,
    FormComponentType.Textarea,
    FormComponentType.Number,
    FormComponentType.AutoComplete,
  ].includes(type);
}

/**
 * Returns `true` if the element has any child element with required validation in the tree
 *
 * @export
 * @param {IElementSchema} element
 * @returns {boolean}
 */
export function hasRequiredValidationInTree(element: IElementSchema): boolean {
  const { elements, validation } = element;
  return (
    hasRequiredValidation(validation) || (!!elements && elements.some((elmt) => hasRequiredValidationInTree(elmt)))
  );
}
/**
 * Generates the default form state object with the given form schema.
 * If `noCheck` is passed as `true`, it will return the formState with `validated` flag all set to `false`.
 *
 * @param {IFormSchema} schema
 * @param {boolean} noCheck
 * @returns {IFormState}
 */
export const createFormStateFromSchema = (schema: IFormSchema, noCheck = false) => {
  function validated(elements: IElementSchema[]): boolean {
    if (elements.length === 0) {
      return !noCheck;
    }
    return elements.every(({ elements: elmts, validation }) => {
      if (elmts) {
        return validated(elmts);
      } else {
        return noCheck ? false : !hasRequiredValidation(validation);
      }
    });
  }
  return {
    currentPosition: { sectionIndex: 0, pageIndex: 0 },
    sections: schema.sections.map(({ pages }) => {
      const pagesStatus = pages.map(({ elements }) => ({
        validated: validated(elements),
        showError: false,
      }));
      const sectionStatus = {
        validated: pagesStatus.every(({ validated }) => validated),
        expanded: false,
        pages: pagesStatus,
      };
      return sectionStatus;
    }),
  };
};

/**
 * Curried function to create flattened list
 *
 * @export
 * @param {string} type
 * @param {(item: any) => boolean} [leafFn]
 * @returns
 */
export function createFlattenSchema(type: string, leafFn?: (item: any) => boolean) {
  return function flattenSchema(acc: any[], item: any) {
    const isLeaf = leafFn ? leafFn(item) : !item[type];
    if (isLeaf) {
      acc.push(item);
    } else {
      acc = acc.concat(item[type].reduce(flattenSchema, []));
    }
    return acc;
  };
}

export const flattenPages = createFlattenSchema('pages');
export const flattenElements = createFlattenSchema('elements', (item) => !item.elements || isPanel(item.type));

/**
 * Flatten `element` object within form schema
 *
 * @export
 * @param {IFormSchema} schema
 * @param {(element: IElementSchema[]) => ObjectSchema<any>} createSchema
 * @returns {ObjectSchema<any>}
 */
export function flattenSchema(schema: IFormSchema, createSchema: (element: IElementSchema[]) => ObjectSchema<any>) {
  const flatPages = schema.sections.reduce(flattenPages as any, []);
  const flatElements = flatPages.reduce(flattenElements as any, []);
  return createSchema(flatElements);
}

/**
 * Creates Yup object schema by page per section
 *
 * @export
 * @param {IFormSchema} schema
 * @param {(config: IElementSchema[]) => ObjectSchema<any>} createSchema
 * @returns {ObjectSchema<any>[][]}
 */
export function flattenSchemaByPage(
  schema: IFormSchema,
  createSchema: (element: IElementSchema[]) => ObjectSchema<any>
) {
  const flatPagesBySections = schema.sections.map((section) => [section].reduce(flattenPages as any, []));
  return flatPagesBySections.map((pages) =>
    pages.map((page) => {
      const elements = [page].reduce(flattenElements as any, []);
      return createSchema(elements);
    })
  );
}

/**
 * Returns the total number of validated pages across sections with the given `formState`
 *
 * @param {IFormState} formState
 * @returns {number}
 */
export function getTotalValidatedPages(formState: IFormState) {
  const flatPages = formState.sections.reduce(flattenPages as any, []);
  return flatPages.filter(({ validated }) => validated).length;
}

export function getTotalValidatedSections(formState: IFormState) {
  return formState.sections.filter(({ validated }) => validated).length;
}

/**
 * Returns the path by appending the `name` to the existing `path`
 *
 * @export
 * @param {string} name
 * @param {string} [parentPath]
 * @returns
 */
export function getPath(name: string, parentPath?: string) {
  return parentPath ? `${parentPath}.${name}` : name;
}

/**
 * Returns the string regex format path if `repeatable` flag is set to `true`
 *
 * @export
 * @param {string} name
 * @param {string} [parentPath]
 * @param {boolean} [repeatable=false]
 * @returns
 */
export function getPathWithRepeatable(name: string, parentPath?: string, repeatable = false) {
  if (!repeatable) {
    return getPath(name, parentPath);
  }
  // return string regex format
  return parentPath ? `${parentPath}\\[\\d+\\]\\.${name}` : name;
}

/**
 * Returns an array of path string for the element which contains the `predicate` within the given the form schema.
 *
 * @export
 * @param {IFormSchema} schema
 * @param {Partial<IElementSchema>} predicate
 * @returns {string[]}
 */
export function getDataPathForFormSchema(schema: IFormSchema, predicate: Partial<IElementSchema>): string[] {
  const findPath = (elements: IElementSchema[], paths: string[], parentName?: string, parentRepeatable = false) => {
    elements
      .filter((element) =>
        Object.keys(predicate).every(
          (key) => element[key as keyof IElementSchema] === predicate[key as keyof IElementSchema]
        )
      )
      .forEach((element: IElementSchema) =>
        paths.push(getPathWithRepeatable(element.name, parentName, parentRepeatable))
      );

    elements
      .filter(({ elements }) => !!elements)
      .forEach(({ name, elements = [], repeatable }) => {
        findPath(elements, paths, getPathWithRepeatable(name, parentName, parentRepeatable), repeatable);
      });
  };

  const paths: string[] = [];

  schema.sections.forEach((section: ISectionSchema) => {
    return section.pages.forEach((page: IPageSchema) => {
      findPath(page.elements, paths);
    });
  });

  return paths;
}

/**
 * Returns an array of field path by searching the matching form data field given an array of encrypted fields.
 * An array of encrypted fields can include regex string to handle repeatable container data.
 *
 * @export
 * @param {IFormData} formData
 * @param {string[]} encryptedFields
 * @returns {string[]}
 */
export function getEncryptedFields(formData: IFormData, encryptedFields: string[]): string[] {
  const getFields = (fieldData: IFormData, paths: string[], parentName = '') => {
    if (Array.isArray(fieldData)) {
      fieldData.forEach((field, index) => {
        getFields(field, paths, `${parentName}[${index}]`);
      });
    } else if (typeof fieldData === 'object') {
      Object.keys(fieldData).forEach((key) => {
        const field = fieldData[key];
        getFields(field, paths, getPath(key, parentName));
      });
    } else if (
      parentName &&
      encryptedFields.some((fieldPath) => {
        if (fieldPath.indexOf('\\d+') > -1) {
          return new RegExp(fieldPath).test(parentName);
        }

        return parentName === fieldPath;
      })
    ) {
      paths.push(parentName);
    }
  };

  const paths: string[] = [];

  getFields(formData, paths);

  return paths;
}

/**
 * Serialize form data before sending to the server.
 * This is mainly for date object to convert to ISO string.
 *
 * @param {IFormData} formData
 * @returns {IFormData}
 */
export function serializeFormData(formData: IFormData) {
  return JSON.parse(JSON.stringify(formData));
}

/**
 * Merge `currentPosition` into form state
 *
 * @export
 * @param {IFormState} formState
 * @param {ICurrentPosition} currentPosition
 * @returns {IFormState}
 */
export function prepareFormState(formState: IFormState, currentPosition: ICurrentPosition) {
  return {
    ...formState,
    currentPosition: { ...currentPosition },
  };
}

/**
 * Returns the final form data schema to be sent to the server
 *
 * @export
 * @param {IFormData} formData
 * @param {string[]} encryptedFields
 * @returns
 */
export function prepareFormDataSchema(formData: IFormData, encryptedFields: string[]) {
  const parsedFormData = formatDateOfBirth(formData);
  const encryptFields = getEncryptedFields(parsedFormData, encryptedFields);
  return { data: serializeFormData(parsedFormData), encryptFields };
}

/**
 * Formats the date of birth (dob) in the input data to YYYY-MM-DD format.
 *
 * @param {IFormData} data - The form data object containing user information.
 * @returns {IFormData} A new object with the same data, but with the dob formatted if present.
 *
 * @description
 * This function checks if the input data contains a 'dob' (date of birth) field.
 * If present, it formats the date to 'YYYY-MM-DD' using Moment.js.
 * The function does not modify the original object, but returns a new object with the formatted date.
 * If 'dob' is not present or is falsy, the original data object is returned unchanged.
 *
 * @example
 * const data = { first_name: "John", dob: new Date("1990-05-15T00:00:00") };
 * const formattedData = formatDateOfBirth(data);
 * // formattedData = { first_name: "John", dob: "1990-05-15" }
 */
export function formatDateOfBirth(data: IFormData): IFormData {
  if (data?.dob) {
    return {
      ...data,
      dob: extractDate(data.dob),
    };
  }
  return data;
}

/**
 * Extracts the date part from a full ISO datetime string.
 *
 * @param {string} datetime - The datetime string in ISO format.
 * @returns {string} The extracted date in 'YYYY-MM-DD' format.
 *
 * @description
 * This function takes an ISO datetime string (e.g., '2003-01-21T02:00:00.000Z')
 * and returns only the date part (e.g., '2003-01-21'). It splits the string at the 'T'
 * to separate the date from the time, returning just the date portion.
 *
 * @example
 * const date = extractDate("2003-01-21T02:00:00.000Z");
 * // date = "2003-01-21"
 */
function extractDate(datetime: string | Date): string {
  if (typeof datetime === 'string' && datetime.split('T').length) {
    return datetime.split('T')[0];
  } else {
    return dayjs(datetime).format('YYYY-MM-DD');
  }
}

/**
 * Check if there are any changes to the form data compared to the initial data
 *
 * @export
 * @param {IFormData} initialData
 * @param {IFormData} formData
 * @returns {boolean}
 */
export function isFormDirty(initialData: IFormData, formData: IFormData) {
  return !isEqual(formData, initialData);
}

/**
 * Recursively unset parent if the value is empty
 *
 * @export
 * @param {{ [key: string]: any }} obj
 * @param {string} path
 * @returns
 */
export function unsetParent(obj: { [key: string]: any }, path: string) {
  const pathArr = path.split('.');
  while (pathArr.length) {
    let newPath: string | null = pathArr.join('.');
    let value = get(obj, newPath);
    if (isEmpty(value)) {
      unset(obj, newPath);
      // check if the path is array item, if so access the array itself
      // and check if the array is empty or all members are empty, then unset
      // before moving up to the parent
      const result = /(^.+)\[\d+\]$/.exec(newPath);
      newPath = result && result[1];
      if (newPath) {
        value = get(obj, newPath);
        if (isEmpty(value) || value.every((item: any) => isEmpty(item))) {
          unset(obj, newPath);
        }
      }
    }
    pathArr.pop();
  }
  return obj;
}

function isFieldError(fieldError: FieldError | FieldErrors<Record<string, any>>): fieldError is FieldError {
  return (
    !!fieldError && (fieldError as FieldError).type !== undefined && (fieldError as FieldError).message !== undefined
  );
}

export function getFormErrorLocations(formErrors: IFormErrors): { sectionIndex: number; pageIndex: number }[] {
  return formErrors.sections.reduce((result: any[], item, sectionIndex) => {
    if (item.pages.length) {
      const pageErrors = item.pages.reduce((pageResult: any[], pageItem, pageIndex) => {
        if (Object.keys(pageItem).length) {
          return [...pageResult, { sectionIndex, pageIndex }];
        }
        return [...pageResult];
      }, []);
      if (pageErrors.length) {
        return [...result, ...pageErrors];
      }
    }
    return [...result];
  }, []);
}

/**
 * Returns `true` if `formErrors` contains the particular validation type exists in the tree
 *
 * @export
 * @param {FieldErrors<Record<string, any>>} formErrors
 * @param {ValidationRuleType} type
 * @returns
 */
export function hasFormErrorsByType(formErrors: FieldErrors<Record<string, any>>, type: ValidationRuleType) {
  const hasFormError = (formError: FieldErrors<Record<string, any>>) => {
    if (isFieldError(formError)) {
      return formError.type === type;
    }

    return !!formError && Object.values(formError).some(hasFormError);
  };

  return isObject(formErrors) && Object.values(formErrors).some(hasFormError);
}

export const hasFormErrors = (formErrors: IFormErrors) => {
  return (
    formErrors &&
    formErrors.sections.length &&
    formErrors.sections.reduce((result, section) => {
      if (result) {
        return result;
      }
      return section.pages.some((page) => {
        return !!Object.keys(page).length;
      });
    }, false)
  );
};

type FindDataPathForFormSchemaPredicate = Partial<
  Pick<IElementSchema, 'type' | 'name' | 'canSaveToProfile' | 'documentType'>
>;

/**
 * Get the path string (key path for form data) of the first element that matches `predicate`.
 *
 * @param {IFormSchema} schema Schema of the form
 * @param {FindDataPathForFormSchemaPredicate} predicate Predicate object which contains a subset of the IElementSchema
 * @returns
 */
export function findDataPathForFormSchema(
  schema: IFormSchema,
  predicate: FindDataPathForFormSchemaPredicate
): Optional<string> {
  const findPath = (elements: IElementSchema[], parentName?: string): Optional<string> => {
    const element: Optional<IElementSchema> = find(elements, { ...predicate });
    if (element) {
      return getPath(element.name, parentName);
    }

    let childPath;
    elements
      .filter(({ type }) => isPanel(type))
      .some(({ name, elements = [] }) => {
        childPath = findPath(elements, name);
        return !!childPath;
      });

    return childPath ? getPath(childPath, parentName) : childPath;
  };

  let path;

  schema.sections.some((section: ISectionSchema) => {
    return section.pages.some((page: IPageSchema) => {
      path = findPath(page.elements);
      return !!path;
    });
  });

  return path;
}

/**
 * Convert IMasterTableData to IPersona
 *
 * @param {IMasterTableData} item - Master table data item
 * @returns {IPersona}
 */
export const convertMasterTableDataToPersona = (item: IMasterTableData): IPersona => ({
  id: item.id,
  name: item.displayText,
  tooltip: get(item, 'additionalInfo.tooltipText'),
  imageUrl: get(item, 'additionalInfo.photoUrl'),
});

export function getFormStatusLabel(formStatus: FormStatus, isFormDeleted?: boolean): string {
  switch (formStatus) {
    case FormStatus.NotStarted || isFormDeleted:
      return 'Not Started';
    case FormStatus.InProgress:
      return 'In Progress';
    case FormStatus.NeedsCorrection:
      return 'Needs Correction';
    case FormStatus.Completed:
      return 'Completed';
    case FormStatus.Declined:
      return 'Declined';
    case FormStatus.Flagged:
      return 'Flagged';
    case FormStatus.Resubmitted:
      return 'Resubmitted';
    default:
      console.error('FormStatus NOT implemented!');
      return '';
    // throw new Error('not implemented');
  }
}

/**
 * Returns FormType based on the given StageType
 *
 * @export
 * @param {StageType} stageType
 * @returns
 */
export function getFormTypeByStageType(stageType?: StageType) {
  if (!stageType) {
    return FormType.APPLICATION;
  }
  return CERTIFICATION_STAGE_TYPES.includes(stageType) ? FormType.QUIZ : FormType.APPLICATION;
}

/**
 * Returns the score of the quiz in percentage given the form schema and form data.
 *
 * @export
 * @param {IFormSchema} schema
 * @param {IFormData} formData
 * @returns
 */
export function getQuizResult(schema: IFormSchema, formData: IFormData) {
  const quizPath = findDataPathForFormSchema(schema, { name: QUIZ_FIELD_NAME });
  if (!quizPath) {
    // This case should be avoided by previewing the form before publishing.
    return 0;
  }
  const quizData = get(formData, quizPath, {});
  // Only the first quiz component in the first page in Form Builder is the scope of the result.
  const quizElements: IElementSchema[] = get(schema, `${QUIZ_ELEMENTS_PATH}[0].elements`, []);
  const total = quizElements.length;
  const result = quizElements.reduce((acc, { name, answer: correctAnswer }) => {
    const answer = get(quizData, name);
    if (isEqual(answer, correctAnswer)) {
      acc += 1;
    }
    return acc;
  }, 0);
  return Math.round((result / total) * 100);
}

/**
 * Calculate form completion percentage
 * Copied from profile-strength-tile.tsx
 *
 * @param {IMasterTableData} item - Master table data item
 * @returns {IPersona}
 */
export const calculateTotalCompletedPercent = (
  formSchema?: IFormSchema,
  formPayload?: IFormPayload,
  calculateBasedOnTopLevelOnly?: boolean
): number => {
  if (!formPayload || !formSchema) {
    return 0;
  }

  const allFields = () => {
    const fields: any = {};
    const hiddenFields: string[] = [];
    const createElementMapByPaths = (elements: IElementSchema[], parentPath?: string) => {
      elements.forEach((element) => {
        const { type, name, elements, visible } = element;
        const path = getPath(name, parentPath);
        if (visible) {
          hiddenFields.push(name);
        }
        if (elements) {
          createElementMapByPaths(elements, path);
        } else if (!visible && type !== FormComponentType.Label) {
          fields[path] = name;
        }
      });
    };

    formSchema.sections.forEach(({ pages }) => pages.forEach(({ elements }) => createElementMapByPaths(elements)));
    return { fields, hiddenFields };
  };
  const totalFields = Object.keys(allFields().fields).length;

  const nestedLoop = (obj: any, hiddenFields: string[]) => {
    const res: any = {};
    let cnt = 0;
    function recurse(obj: any) {
      for (const key in obj) {
        const value = obj[key];
        if (value !== undefined) {
          const isMultiValue = Array.isArray(value) && typeof value[0] !== 'object';
          if (value && typeof value === 'object' && !isMultiValue) {
            const isRepeatableValue = Array.isArray(value) && typeof value[0] === 'object';
            recurse(isRepeatableValue ? value[0] : value);
          } else if (!isEmpty(value) && !hiddenFields.includes(key)) {
            cnt += 1;
            res[`${key}-${cnt}`] = value;
          }
        }
      }
    }
    recurse(obj);
    return res;
  };

  const flatData = nestedLoop(formPayload?.data, allFields().hiddenFields);

  if (calculateBasedOnTopLevelOnly) {
    const topKeys = [];
    let topKey;
    for (const key in allFields().fields) {
      topKey = key.split('.')[0];
      if (topKeys.indexOf(topKey) === -1) {
        topKeys.push(topKey);
      }
    }

    let totalCompleted = 0;
    for (const i in topKeys) {
      if (get(formPayload?.data, topKeys[i]) !== undefined) {
        totalCompleted++;
      }
    }

    return Math.min(100, Math.floor((totalCompleted / topKeys.length) * 100));
  }

  const totalCompletedFieldsCount = Object.keys(flatData).length;

  return Math.min(100, Math.floor((totalCompletedFieldsCount / totalFields) * 100));
};

export const replacePathSeparator = (value: string) => value.replace(PATH_SEPARATOR, '.');

function concatToUniversityDisplayName(values: string[], result = ''): string {
  if (!values.length) {
    return result;
  }
  const [current, ...rest] = values;
  const hasSeparator = !!rest.length;
  const newValue = current ? `${current}${hasSeparator ? ', ' : ''}` : '';
  return concatToUniversityDisplayName(rest, `${result}${newValue}`);
}

/**
 * Remove unwanted data from university name that are behind "|" char
 *
 * @param name
 * @returns
 */
export function formatUniversityName(name: string) {
  return concatToUniversityDisplayName(name.split('|').slice(0, 2));
}
